import { addDays } from "date-fns"
import { startOfTodayUTC } from "~/utils/datetime"

type SortCell = { forecast: number; stockoutLoss: number }

/**
 * Forecast-weighted availability (0..1) over shown months ending on/after `today + leadTimeDays`
 * (reachable by a buy placed today), else all shown months. No demand is ∞, so it sorts last.
 */
export function actionableAvailability(
  cells: SortCell[],
  months: { lastDay: Date }[],
  leadTimeDays: number,
  today: Date,
): number {
  const arrival = addDays(today, leadTimeDays)
  const reachable = cells.filter((_, i) => {
    const month = months[i]
    return month !== undefined && month.lastDay >= arrival
  })
  const counted = reachable.length ? reachable : cells
  const forecast = counted.reduce((sum, c) => sum + c.forecast, 0)
  if (!(forecast > 0)) return Number.POSITIVE_INFINITY
  const lost = counted.reduce((sum, c) => sum + c.stockoutLoss, 0)
  return 1 - lost / forecast
}

// Explicit rather than `a - b`, which is NaN for two all-empty variants (∞ - ∞).
export function compareLowest(a: number, b: number): number {
  if (a === b) return 0
  return a < b ? -1 : 1
}

/** Lowest `actionableAvailability` first; stable, so ties keep the incoming order. */
export function sortWorstFirst<T>(
  items: T[],
  cellsOf: (item: T) => SortCell[],
  leadTimeOf: (item: T) => number,
  months: { lastDay: Date }[],
): T[] {
  const today = startOfTodayUTC()
  return items
    .map((item) => ({
      item,
      score: actionableAvailability(cellsOf(item), months, leadTimeOf(item), today),
    }))
    .sort((a, b) => compareLowest(a.score, b.score))
    .map(({ item }) => item)
}
